import { useEffect, useRef, useState } from "react";
import type { TerminalConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { hubExerciseHref } from "../lib/hostConfig";
import { activeTtsLang } from "../lib/locale";
import { SkipButton } from "../ui/SkipButton";

/**
 * Hand-crafted ASCII-art emblem for the terminal's right-hand panel — a
 * hexagonal security crest with a bold block "N" monogram. Built from
 * hexagon-outline punctuation (`. + = / \ |`) PLUS solid block glyphs for the
 * letterform itself (not fine diagonals), so the letter stays CRISP at the
 * sizes the kiosk column actually has room for — the earlier all-punctuation
 * version washed out into an illegible grey smudge once scaled to fit
 * (2026-07-25 feedback: "doesn't look similar" to the reference mock). This
 * version is deliberately larger/denser (hexagon border + dot-grid texture
 * rows + wordmark) to read as a genuine crest, not a tiny icon.
 * Pure text, no image/network asset, so it never needs the CSP/font
 * exceptions 3D text does.
 */
// Every row is laid out on a fixed 33-column grid, centre column 16. Keep the
// leading spaces of the first row: `.trim()` removed them and shifted the top bar.
const TERMINAL_ASCII_LOGO = String.raw`
           .+=======+.
        .+=============+.
     .+===================+.
    .+=====================+.
   /                         \
  /     .   .   .   .   .     \
 |                             |
 |       ██          ██        |
 |       ████        ██        |
 |       ██  ██      ██        |
 |       ██    ██    ██        |
 |       ██      ██  ██        |
 |       ██        ████        |
 |       ██          ██        |
 |                             |
 |       N O R T H B A Y       |
 |                             |
  \     .   .   .   .   .     /
   \                         /
    '+=====================+'
     '+===================+'
        '+=============+'
           '+=======+'
`.replace(/^\n+/, "").replace(/\s+$/, "");

type TermLineIcon = "wave" | "bell" | "shield" | "lock" | "info";

/** Cheap keyword heuristic so plain scenario copy (`config.lines: string[]`)
 *  still gets a distinct icon per line, matching the reference mock's
 *  icon-per-row layout, without adding a new per-line schema field. */
function iconForLine(line: string): TermLineIcon {
  const l = line.toLowerCase();
  if (l.includes("welcome") || l.includes("goes to the board") || l.includes("hi ")) return "wave";
  if (l.includes("unattended") || l.includes("intercom") || l.includes("access request")) return "bell";
  if (l.includes("badge") || l.includes("secured floor") || l.includes("verified")) return "shield";
  if (l.includes("standard") || l.includes("vigilance") || l.includes("advantage")) return "lock";
  return "info";
}

function TermLineIconGlyph({ icon }: { icon: TermLineIcon }) {
  switch (icon) {
    case "wave":
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.7">
          <circle cx="12" cy="7.5" r="3.2" />
          <path d="M5 20c0-3.5 3.2-6 7-6s7 2.5 7 6" strokeLinecap="round" />
        </svg>
      );
    case "bell":
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.7">
          <path d="M12 4a5 5 0 0 0-5 5v3.2c0 1-.4 2-1.1 2.7L5 16h14l-.9-1.1A4 4 0 0 1 17 12.2V9a5 5 0 0 0-5-5Z" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M9.5 18.5a2.5 2.5 0 0 0 5 0" strokeLinecap="round" />
        </svg>
      );
    case "shield":
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.7">
          <path d="M12 3.5 19 6v6c0 4.5-3 7.5-7 8.5-4-1-7-4-7-8.5V6Z" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M9 12.3l2.1 2.1L15.3 10" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "lock":
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.7">
          <rect x="5.5" y="11" width="13" height="9" rx="1.6" />
          <path d="M8 11V7.5a4 4 0 0 1 8 0V11" strokeLinecap="round" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.7">
          <circle cx="12" cy="12" r="8.5" />
          <path d="M12 10.5v6M12 7.2h.01" strokeLinecap="round" />
        </svg>
      );
  }
}

/**
 * A static informational kiosk/terminal (e.g. a reception welcome screen
 * standing in for a receptionist who can't be shown as a character). Starts
 * OFF with a power prompt — a click is deterministic proof the learner
 * wants to engage — then reveals `config.lines` sequentially (spoken +
 * captioned, same pattern as Consequence.tsx) before a single Continue.
 * No branching: this is context/flavor, not a decision.
 */
export function Terminal({
  config,
  onComplete,
  onChoose,
  onPowerOn,
  tone,
  tag,
  kicker,
  skipPowerOn,
}: {
  config: TerminalConfig;
  onComplete: () => void;
  /** Choice id when `config.choices` is set. Choices with `href` navigate themselves. */
  onChoose?: (id: string) => void;
  /** Fired the moment the learner switches the terminal on (used by App to
   *  unlock interactions gated on "terminal must be on first", e.g. the desk
   *  sticky-note hotspot in office-under-attack). */
  onPowerOn?: () => void;
  /** Ending-mode outcome banner (good/warn/bad), reusing the same tone
   *  vocabulary as Ending.tsx's `end-tag--{band}` styling — lets the
   *  Episode 12 finale render through THIS terminal chrome (2026-07-26:
   *  "redesign this visually boring scene ... display it on the terminal
   *  screen from Scene 1") instead of a separate plain bulletin card, for
   *  all three ending branches, not just the worst one. Also retints the
   *  whole kiosk glow (border/text/ascii) via `.terminal--tone-*`. */
  tone?: "good" | "warn" | "bad";
  /** Small pill next to the kicker, e.g. "NO COMPROMISE" / "FULL COMPROMISE". */
  tag?: string;
  /** Eyebrow line above the tag, e.g. "COMPANY SECURITY BULLETIN". */
  kicker?: string;
  /** Skip the power-on prompt and start already on — used by the ending,
   *  which is narratively "you're looking at this same terminal again", not
   *  switching it on fresh. */
  skipPowerOn?: boolean;
}) {
  const [on, setOn] = useState(!!skipPowerOn);
  const [shown, setShown] = useState(0);
  const started = useRef(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    // Warm the TTS cache for EVERY line at mount (before the learner even
    // clicks power-on), so the first line's voice starts the instant they
    // switch it on instead of paying a cold network+TTS round-trip. This is
    // what removes the "dead lag before the voice starts" on this scene.
    const voiceOpts = { voice: config.voiceId ?? "onyx", lang: config.language ?? activeTtsLang() };
    config.lines.forEach((l) => audio.prefetchSpeech(l, voiceOpts));
    return () => {
      alive.current = false;
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!on || started.current) return;
    started.current = true;
    if (skipPowerOn) audio.sceneTransition();
    const voiceOpts = { voice: config.voiceId ?? "onyx", lang: config.language ?? activeTtsLang() };
    let i = 0;
    // PIPELINED TTS: kick off the fetch for the NEXT line as soon as the
    // current one starts playing, so by the time this line's audio ends the
    // next line's blob is already cached (fetchTts checks the cache first).
    // Without this, each line paid its own network+TTS-generation latency
    // back-to-back — a long, silent "pause" between lines with nothing
    // changing on screen. Only the natural narration length remains now.
    audio.prefetchSpeech(config.lines[0], voiceOpts);
    const next = async () => {
      if (!alive.current || !config.lines[i]) return;
      setShown(i + 1);
      if (config.lines[i + 1]) audio.prefetchSpeech(config.lines[i + 1], voiceOpts);
      await audio.speak(config.lines[i], voiceOpts);
      i += 1;
      if (alive.current) next();
    };
    next();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on]);

  const done = shown >= config.lines.length;

  const skip = () => {
    audio.cancelSpeech();
    setShown(config.lines.length);
    if (!config.choices?.length) onComplete();
  };

  return (
    <div className={`terminal ${tone ? `terminal--tone-${tone}` : ""}`}>
      <div className="term-bezel">
        <span className="term-bezel-cam" aria-hidden />
        {!on ? (
          <button
            type="button"
            className="screen-power term-power-inset"
            onClick={() => {
              audio.powerOn();
              setOn(true);
              onPowerOn?.();
            }}
          >
            <span className="screen-power-ico" aria-hidden>
              <svg viewBox="0 0 24 24" width="100%" height="100%" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M12 3v8" strokeLinecap="round" />
                <path d="M6.3 6.3a8 8 0 1 0 11.4 0" strokeLinecap="round" />
              </svg>
            </span>
            <span className="screen-power-label">{config.offLabel ?? "Switch on the terminal"}</span>
          </button>
        ) : (
          <div className="term-screen term-screen--kiosk">
            {(config.title || tone) && (
              <div className="term-head">
                {tone && (
                  <div className="term-head-status">
                    {tag && <span className={`term-head-tag term-head-tag--${tone}`}>{tag}</span>}
                    {kicker && <span className="term-head-kicker">{kicker}</span>}
                  </div>
                )}
                {config.title && (
                  <div className="term-head-top">
                    <span className="term-head-badge" aria-hidden>N</span>
                    <span className="term-head-title">{config.title}</span>
                  </div>
                )}
                <div className="term-head-rule" aria-hidden />
              </div>
            )}
            <div className="term-info">
              <div className="term-lines">
                {config.lines.slice(0, shown).map((l, i) => (
                  <div key={i} className="term-line term-line--row">
                    <span className="term-line-icon" aria-hidden>
                      <TermLineIconGlyph icon={iconForLine(l)} />
                    </span>
                    <p className="term-line-text">{l}</p>
                  </div>
                ))}
                {!done && shown < config.lines.length && (
                  <span className="term-caret-blink" aria-hidden>{"\u2588"}</span>
                )}
              </div>
              {done && (
                <div className="term-tagline">
                  <span className="term-line-icon" aria-hidden>
                    <TermLineIconGlyph icon="lock" />
                  </span>
                  <span>SECURITY IS OUR STANDARD.<br />VIGILANCE IS OUR ADVANTAGE.</span>
                </div>
              )}
            </div>
            <div className="term-ascii" aria-hidden>
              <span className="term-ascii-corner term-ascii-corner--tl">+</span>
              <span className="term-ascii-corner term-ascii-corner--tr">+</span>
              <span className="term-ascii-dots" />
              <pre className="term-ascii-art">{TERMINAL_ASCII_LOGO}</pre>
              <span className="term-ascii-corner term-ascii-corner--bl">+</span>
              <span className="term-ascii-corner term-ascii-corner--br">+</span>
            </div>
            <div className="term-cta-row">
              {done && config.choices?.length ? (
                <div className="term-choices">
                  {config.choices.map((choice) => (
                    <button
                      key={choice.id}
                      type="button"
                      className="btn term-continue term-choice"
                      onClick={() => {
                        audio.cancelSpeech();
                        if (choice.href) {
                          window.location.href = hubExerciseHref(choice.href);
                          return;
                        }
                        onChoose?.(choice.id);
                      }}
                    >
                      <span className="term-choice-label">{choice.label}</span>
                      {choice.blurb && <span className="term-choice-blurb">{choice.blurb}</span>}
                    </button>
                  ))}
                </div>
              ) : (
                <button
                  type="button"
                  className={`btn term-continue ${done ? "" : "term-continue--wait"}`}
                  disabled={!done}
                  onClick={onComplete}
                >
                  <span className="term-cta-chevron" aria-hidden>{"\u203A"}</span>
                  {done ? config.continueLabel ?? "Continue" : "See why this matters \u2192"}
                </button>
              )}
              <SkipButton onClick={skip} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
